import React, { useEffect, useState } from 'react';
import { SearchIcon, RefreshCwIcon, CheckIcon, XIcon, AlertCircleIcon, FileTextIcon, BotIcon, XCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function ReconciliationWorkspace({
  reconciliation,
  statements = {
    statements: []
  },
  onViewDiscrepancies,
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('all'); // all, matched, unmatched
  const [searchTerm, setSearchTerm] = useState('');
  const [isRemiOpen, setIsRemiOpen] = useState(false);
  // Initialize with seeded statement items instead of empty array
  const initialStatementItems = statements.statements && statements.statements.length > 0 ? statements.statements : [{
    id: 'STMT-001',
    invoiceNumber: 'INV-123',
    invoiceDate: '2023-01-15',
    amount: 1250.75,
    description: 'Office Supplies',
    status: 'Unmatched'
  }, {
    id: 'STMT-002',
    invoiceNumber: 'INV-124',
    invoiceDate: '2023-01-22',
    amount: 2340.0,
    description: 'IT Equipment',
    status: 'Unmatched'
  }, {
    id: 'STMT-003',
    invoiceNumber: 'INV-125',
    invoiceDate: '2023-01-30',
    amount: 450.25,
    description: 'Maintenance',
    status: 'Unmatched'
  }, {
    id: 'STMT-004',
    invoiceNumber: 'INV-126',
    invoiceDate: '2023-01-28',
    amount: 1875.5,
    description: 'Office Furniture',
    status: 'Unmatched'
  }, {
    id: 'STMT-005',
    invoiceNumber: 'INV-127',
    invoiceDate: '2023-01-18',
    amount: 725.3,
    description: 'Cleaning Services',
    status: 'Unmatched'
  }, {
    id: 'STMT-006',
    invoiceNumber: 'INV-128',
    invoiceDate: '2023-01-25',
    amount: 2500.0,
    description: 'Software Licenses',
    status: 'Unmatched'
  }, {
    id: 'STMT-007',
    invoiceNumber: 'INV-129',
    invoiceDate: '2023-01-05',
    amount: 1350.0,
    description: 'Marketing Materials',
    status: 'Unmatched'
  }, {
    id: 'STMT-008',
    invoiceNumber: 'INV-130',
    invoiceDate: '2023-01-12',
    amount: 890.45,
    description: 'Office Repairs',
    status: 'Unmatched'
  }];
  const [statementItems, setStatementItems] = useState(initialStatementItems);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatementItem, setSelectedStatementItem] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // Sample AP invoices for demo - some match exactly, some partially, some don't match
  const sampleInvoices = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-123',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-15',
    dueDate: '2023-02-15',
    amount: 1250.75,
    status: 'Pending',
    matchedToStatement: false
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-124',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-22',
    dueDate: '2023-02-22',
    amount: 2340.0,
    status: 'Approved',
    matchedToStatement: false
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-125',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-30',
    dueDate: '2023-03-01',
    amount: 450.25,
    status: 'Paid',
    matchedToStatement: false
  }, {
    id: 'INV-2023-004',
    invoiceNumber: 'INV-126',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-28',
    dueDate: '2023-02-28',
    amount: 1875.5,
    status: 'Pending',
    matchedToStatement: false
  }, {
    id: 'INV-2023-005',
    invoiceNumber: 'INV-127',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-18',
    dueDate: '2023-02-18',
    amount: 695.8,
    status: 'Approved',
    matchedToStatement: false
  }, {
    id: 'INV-2023-006',
    invoiceNumber: 'INV-128',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-25',
    dueDate: '2023-02-25',
    amount: 2350.0,
    status: 'Pending',
    matchedToStatement: false
  }, {
    id: 'INV-2023-007',
    invoiceNumber: 'INV-131',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-10',
    dueDate: '2023-02-10',
    amount: 520.75,
    status: 'Paid',
    matchedToStatement: false
  }, {
    id: 'INV-2023-008',
    invoiceNumber: 'INV-132',
    vendor: reconciliation.vendor,
    vendorId: reconciliation.vendorId,
    invoiceDate: '2023-01-20',
    dueDate: '2023-02-20',
    amount: 1200.0,
    status: 'Approved',
    matchedToStatement: false
  }];
  useEffect(() => {
    // Simulate loading data
    setIsLoading(true);
    setTimeout(() => {
      setInvoices(sampleInvoices);
      setIsLoading(false);
    }, 1000);
  }, []);
  const handleAutoMatch = () => {
    setIsLoading(true);
    // Simulate auto-matching process
    setTimeout(() => {
      // Simple matching algorithm based on invoice number and amount
      const updatedStatements = statementItems.map(statement => {
        const matchingInvoice = sampleInvoices.find(invoice => invoice.invoiceNumber === statement.invoiceNumber && Math.abs(invoice.amount - statement.amount) < 0.01);
        if (matchingInvoice) {
          return {
            ...statement,
            status: 'Matched',
            matchedInvoiceId: matchingInvoice.id
          };
        }
        // Check for partial matches (invoice number matches but amount differs)
        const partialMatch = sampleInvoices.find(invoice => invoice.invoiceNumber === statement.invoiceNumber);
        if (partialMatch) {
          return {
            ...statement,
            status: 'Partial Match',
            matchedInvoiceId: partialMatch.id,
            discrepancyAmount: statement.amount - partialMatch.amount
          };
        }
        return statement;
      });
      // Update invoices to mark which ones are matched
      const updatedInvoices = sampleInvoices.map(invoice => {
        const matchedStatement = updatedStatements.find(statement => statement.matchedInvoiceId === invoice.id);
        return {
          ...invoice,
          matchedToStatement: !!matchedStatement
        };
      });
      setStatementItems(updatedStatements);
      setInvoices(updatedInvoices);
      setIsLoading(false);
    }, 1500);
  };
  const handleRemiAssist = () => {
    setIsRemiOpen(true);
  };
  const handleCloseRemi = () => {
    setIsRemiOpen(false);
  };
  const handleManualMatch = () => {
    if (!selectedStatementItem || !selectedInvoice) return;
    // Update the selected statement item
    const updatedStatements = statementItems.map(statement => {
      if (statement.id === selectedStatementItem.id) {
        const isExactMatch = Math.abs(statement.amount - selectedInvoice.amount) < 0.01;
        return {
          ...statement,
          status: isExactMatch ? 'Matched' : 'Partial Match',
          matchedInvoiceId: selectedInvoice.id,
          discrepancyAmount: isExactMatch ? 0 : statement.amount - selectedInvoice.amount
        };
      }
      return statement;
    });
    // Update the selected invoice
    const updatedInvoices = invoices.map(invoice => {
      if (invoice.id === selectedInvoice.id) {
        return {
          ...invoice,
          matchedToStatement: true
        };
      }
      return invoice;
    });
    setStatementItems(updatedStatements);
    setInvoices(updatedInvoices);
    setSelectedStatementItem(null);
    setSelectedInvoice(null);
  };
  const handleIgnoreStatement = () => {
    if (!selectedStatementItem) return;
    // Mark the statement item as ignored
    const updatedStatements = statementItems.map(statement => {
      if (statement.id === selectedStatementItem.id) {
        return {
          ...statement,
          status: 'Ignored'
        };
      }
      return statement;
    });
    setStatementItems(updatedStatements);
    setSelectedStatementItem(null);
  };
  const getStatementStatusColor = status => {
    switch (status) {
      case 'Matched':
        return 'bg-green-100 text-green-800';
      case 'Partial Match':
        return 'bg-yellow-100 text-yellow-800';
      case 'Ignored':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };
  const filteredStatements = statementItems.filter(statement => {
    // Tab filter
    if (activeTab === 'matched' && statement.status !== 'Matched') return false;
    if (activeTab === 'unmatched' && statement.status === 'Matched') return false;
    // Search filter
    return statement.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) || statement.description && statement.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const filteredInvoices = invoices.filter(invoice => {
    // If a statement is selected, only show unmatched invoices
    if (selectedStatementItem && invoice.matchedToStatement) return false;
    // Search filter
    return invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) || invoice.vendor.toLowerCase().includes(searchTerm.toLowerCase());
  });
  // Calculate summary statistics
  const matchedCount = statementItems.filter(s => s.status === 'Matched').length;
  const unmatchedCount = statementItems.filter(s => s.status !== 'Matched' && s.status !== 'Ignored').length;
  const partialMatchCount = statementItems.filter(s => s.status === 'Partial Match').length;
  const ignoredCount = statementItems.filter(s => s.status === 'Ignored').length;
  return <div data-id="element-1611">
      <div className="mb-6 flex justify-between items-center" data-id="element-1612">
        <div data-id="element-1613">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-1614">
            Reconciliation Workspace: {reconciliation.vendor}
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1615">
            Statement Period: {reconciliation.statementPeriod}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-1616">
          <button onClick={handleRemiAssist} className="inline-flex items-center px-4 py-2 border border-indigo-500 shadow-sm text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1617">
            <BotIcon className="h-4 w-4 mr-1" data-id="element-1618" />
            Remi AI
          </button>
          <button onClick={handleAutoMatch} disabled={isLoading} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1619">
            <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-1620" />
            Auto-Match
          </button>
          <button onClick={onViewDiscrepancies} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1621">
            <AlertCircleIcon className="h-4 w-4 mr-1" data-id="element-1622" />
            View Discrepancies
          </button>
        </div>
      </div>

      {/* Remi AI Assistant Modal */}
      {isRemiOpen && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1623">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col" data-id="element-1624">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200" data-id="element-1625">
              <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-1626">
                <BotIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-1627" />
                Remi AI - Statement Reconciliation Assistant
              </h3>
              <button onClick={handleCloseRemi} className="text-gray-400 hover:text-gray-500" data-id="element-1628">
                <XCircleIcon className="h-6 w-6" data-id="element-1629" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6" data-id="element-1630">
              <div className="bg-indigo-50 rounded-lg p-4 mb-6 border-l-4 border-indigo-500" data-id="element-1631">
                <p className="text-sm text-indigo-700" data-id="element-1632">
                  <strong data-id="element-1633">Remi:</strong> I've analyzed the statement items and
                  AP invoices for {reconciliation.vendor}. Here's what I found:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-indigo-700 list-disc pl-5" data-id="element-1634">
                  <li data-id="element-1635">
                    4 exact matches found (INV-123, INV-124, INV-125, INV-126)
                  </li>
                  <li data-id="element-1636">
                    2 potential matches with discrepancies in amounts:
                    <ul className="pl-4 mt-1 space-y-1 list-circle" data-id="element-1637">
                      <li data-id="element-1638">
                        INV-127: Statement shows $725.30, AP system shows
                        $695.80 (difference of $29.50)
                      </li>
                      <li data-id="element-1639">
                        INV-128: Statement shows $2,500.00, AP system shows
                        $2,350.00 (difference of $150.00)
                      </li>
                    </ul>
                  </li>
                  <li data-id="element-1640">
                    2 statement items with no matching AP invoices (INV-129,
                    INV-130)
                  </li>
                  <li data-id="element-1641">2 AP invoices not on the statement (INV-131, INV-132)</li>
                </ul>
                <div className="mt-3" data-id="element-1642">
                  <p className="text-sm text-indigo-700" data-id="element-1643">
                    <strong data-id="element-1644">Recommendations:</strong>
                  </p>
                  <ul className="mt-1 space-y-1 text-sm text-indigo-700 list-disc pl-5" data-id="element-1645">
                    <li data-id="element-1646">Auto-match the 4 exact matches</li>
                    <li data-id="element-1647">
                      Review the 2 discrepancies - the $29.50 difference on
                      INV-127 might be a late fee
                    </li>
                    <li data-id="element-1648">
                      Verify if INV-129 and INV-130 should be in the AP system
                    </li>
                    <li data-id="element-1649">
                      Check if INV-131 and INV-132 were processed after this
                      statement period
                    </li>
                  </ul>
                </div>
              </div>
              <div className="space-y-4" data-id="element-1650">
                <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-1651">
                  <h4 className="font-medium text-gray-900 mb-2" data-id="element-1652">
                    Would you like me to:
                  </h4>
                  <div className="space-y-2" data-id="element-1653">
                    <button className="w-full text-left px-4 py-2 bg-indigo-50 hover:bg-indigo-100 rounded-md text-indigo-700 font-medium" data-id="element-1654">
                      Auto-match the 4 exact matches
                    </button>
                    <button className="w-full text-left px-4 py-2 bg-indigo-50 hover:bg-indigo-100 rounded-md text-indigo-700 font-medium" data-id="element-1655">
                      Generate a discrepancy report for review
                    </button>
                    <button className="w-full text-left px-4 py-2 bg-indigo-50 hover:bg-indigo-100 rounded-md text-indigo-700 font-medium" data-id="element-1656">
                      Suggest journal entries for the discrepancies
                    </button>
                    <button className="w-full text-left px-4 py-2 bg-indigo-50 hover:bg-indigo-100 rounded-md text-indigo-700 font-medium" data-id="element-1657">
                      Analyze historical patterns for this vendor
                    </button>
                  </div>
                </div>
                <div className="relative" data-id="element-1658">
                  <input type="text" className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Ask Remi about this reconciliation..." data-id="element-1659" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-1660">
                    <button className="text-indigo-600 hover:text-indigo-500" data-id="element-1661">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" data-id="element-1662">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" data-id="element-1663" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-1664">
              <button onClick={handleCloseRemi} className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1665">
                Close
              </button>
            </div>
          </div>
        </div>}

      {/* Summary Stats */}
      <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-4" data-id="element-1666">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1667">
          <div className="px-4 py-5 sm:p-6" data-id="element-1668">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1669">
              Matched
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-green-600" data-id="element-1670">
              {matchedCount}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1671">
          <div className="px-4 py-5 sm:p-6" data-id="element-1672">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1673">
              Unmatched
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-red-600" data-id="element-1674">
              {unmatchedCount}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1675">
          <div className="px-4 py-5 sm:p-6" data-id="element-1676">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1677">
              Partial Matches
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-yellow-600" data-id="element-1678">
              {partialMatchCount}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1679">
          <div className="px-4 py-5 sm:p-6" data-id="element-1680">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1681">
              Ignored
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-600" data-id="element-1682">
              {ignoredCount}
            </dd>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4" data-id="element-1683">
        <div className="sm:hidden" data-id="element-1684">
          <label htmlFor="tabs" className="sr-only" data-id="element-1685">
            Select a tab
          </label>
          <select id="tabs" name="tabs" className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={activeTab} onChange={e => setActiveTab(e.target.value)} data-id="element-1686">
            <option value="all" data-id="element-1687">All Items</option>
            <option value="matched" data-id="element-1688">Matched</option>
            <option value="unmatched" data-id="element-1689">Unmatched</option>
          </select>
        </div>
        <div className="hidden sm:block" data-id="element-1690">
          <div className="border-b border-gray-200" data-id="element-1691">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-1692">
              <button onClick={() => setActiveTab('all')} className={`${activeTab === 'all' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-1693">
                All Items
              </button>
              <button onClick={() => setActiveTab('matched')} className={`${activeTab === 'matched' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-1694">
                Matched
              </button>
              <button onClick={() => setActiveTab('unmatched')} className={`${activeTab === 'unmatched' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-1695">
                Unmatched
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6" data-id="element-1696">
        <div className="relative rounded-md shadow-sm" data-id="element-1697">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1698">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1699" />
          </div>
          <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by invoice number or description" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1700" />
        </div>
      </div>

      {isLoading ? <div className="flex justify-center items-center py-12" data-id="element-1701">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" data-id="element-1702"></div>
        </div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1703">
          {/* Statement Items */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1704">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-1705">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1706">
                Vendor Statement Items
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-1707">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1708">
                <thead className="bg-gray-50" data-id="element-1709">
                  <tr data-id="element-1710">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1711">
                      Invoice #
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1712">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1713">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1714">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1715">
                  {filteredStatements.length > 0 ? filteredStatements.map(statement => <tr key={statement.id} className={`hover:bg-gray-50 cursor-pointer ${selectedStatementItem?.id === statement.id ? 'bg-indigo-50' : ''}`} onClick={() => setSelectedStatementItem(statement)} data-id="element-1716">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1717">
                          {statement.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1718">
                          {statement.invoiceDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1719">
                          {formatCurrency(statement.amount, 'USD', locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1720">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatementStatusColor(statement.status)}`} data-id="element-1721">
                            {statement.status || 'Unmatched'}
                          </span>
                        </td>
                      </tr>) : <tr data-id="element-1722">
                      <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1723">
                        No statement items found matching your criteria
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* AP Invoices */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1724">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-1725">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1726">
                AP Invoices
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-1727">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1728">
                <thead className="bg-gray-50" data-id="element-1729">
                  <tr data-id="element-1730">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1731">
                      Invoice #
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1732">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1733">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1734">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1735">
                  {filteredInvoices.length > 0 ? filteredInvoices.map(invoice => <tr key={invoice.id} className={`hover:bg-gray-50 cursor-pointer ${selectedInvoice?.id === invoice.id ? 'bg-indigo-50' : ''}`} onClick={() => setSelectedInvoice(invoice)} data-id="element-1736">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1737">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1738">
                          {invoice.invoiceDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1739">
                          {formatCurrency(invoice.amount, 'USD', locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1740">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invoice.status === 'Paid' ? 'bg-green-100 text-green-800' : invoice.status === 'Approved' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1741">
                            {invoice.status}
                          </span>
                        </td>
                      </tr>) : <tr data-id="element-1742">
                      <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1743">
                        No invoices found matching your criteria
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>}

      {/* Action Buttons */}
      {(selectedStatementItem || selectedInvoice) && <div className="mt-6 bg-gray-50 p-4 rounded-md flex justify-between items-center" data-id="element-1744">
          <div data-id="element-1745">
            {selectedStatementItem && <div className="text-sm text-gray-700" data-id="element-1746">
                <span className="font-medium" data-id="element-1747">Selected Statement Item:</span>{' '}
                {selectedStatementItem.invoiceNumber} (
                {formatCurrency(selectedStatementItem.amount, 'USD', locale)})
              </div>}
            {selectedInvoice && <div className="text-sm text-gray-700" data-id="element-1748">
                <span className="font-medium" data-id="element-1749">Selected Invoice:</span>{' '}
                {selectedInvoice.invoiceNumber} (
                {formatCurrency(selectedInvoice.amount, 'USD', locale)})
              </div>}
          </div>
          <div className="flex space-x-3" data-id="element-1750">
            {selectedStatementItem && !selectedInvoice && <button onClick={handleIgnoreStatement} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1751">
                <XIcon className="h-4 w-4 mr-1" data-id="element-1752" />
                Ignore
              </button>}
            {selectedStatementItem && selectedInvoice && <button onClick={handleManualMatch} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-1753">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-1754" />
                Match Selected
              </button>}
            <button onClick={() => {
          setSelectedStatementItem(null);
          setSelectedInvoice(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1755">
              Clear Selection
            </button>
          </div>
        </div>}
    </div>;
}